'use client';

import type { ComponentPropsWithoutRef } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { z } from 'zod';
import type { Locale, Segment } from '@fxtr/i18n';
import { CmsImageSchema, CmsShortTextSchema } from '$cms/types';
import { extendCmsText } from '$cms/index';
import { capitalize } from '$util/basic/capitalize';
import type { AppGarage, AppLocation } from '$page-router/api/getGaragesByLatLong';
import { GDPGarageInfoCard } from '@/components/organisms/GDPGarageInfoCard';
import { GarageMap } from '@/components/organisms/GarageMap';
import { ShowAllToggle } from '@/components/atoms/ShowAllToggle';
import { Col, Row } from '@/components/atoms/Grid';
import { StickyHeaderScrollToMap } from '@/components/molecules/StickyHeaderScrollToMap';
import type { CmsServerComponent } from '@/types/components';

import styles from './index.module.scss';

const GarageDirectoryContentSchema = z.object({
  mapPostCodeText: CmsShortTextSchema,
  mapExpandText: CmsShortTextSchema,
  mapHideText: CmsShortTextSchema,
  stickyHeaderBackToMapLabel: CmsShortTextSchema,
  stickyHeaderTitle: CmsShortTextSchema,
  certifiedImageDesktop: CmsImageSchema,
  certifiedImageMobile: CmsImageSchema,
  reviewsNumberText: CmsShortTextSchema,
  moreInfoCtaLabel: CmsShortTextSchema,
  fixterCNDText: CmsShortTextSchema,
  reviewTitle: CmsShortTextSchema,
  bookingsNumberText: CmsShortTextSchema,
  yearsInBusinessText: CmsShortTextSchema,
  showMoreGaragesToggleLabel: CmsShortTextSchema,
  showLessGaragesToggleLabel: CmsShortTextSchema,
  noGaragesText: CmsShortTextSchema,
});

const getComponentTranslations = (data: unknown, area: string) => {
  const capitalisedArea = capitalize(area);
  const {
    mapPostCodeText,
    mapExpandText,
    mapHideText,
    stickyHeaderBackToMapLabel,
    stickyHeaderTitle,
    certifiedImageDesktop,
    certifiedImageMobile,
    reviewsNumberText,
    moreInfoCtaLabel,
    fixterCNDText,
    reviewTitle,
    bookingsNumberText,
    yearsInBusinessText,
    showMoreGaragesToggleLabel,
    showLessGaragesToggleLabel,
    noGaragesText,
  } = GarageDirectoryContentSchema.parse(data);
  return {
    mapPostCodeText: mapPostCodeText.value,
    mapExpandText: mapExpandText.value,
    mapHideText: mapHideText.value,
    stickyHeaderBackToMapLabel: stickyHeaderBackToMapLabel.value,
    stickyHeaderTitle: extendCmsText(stickyHeaderTitle.value, {
      area: capitalisedArea,
    }),
    certifiedImageDesktop,
    certifiedImageMobile,
    reviewsNumberText: reviewsNumberText.value,
    moreInfoCtaLabel: moreInfoCtaLabel.value,
    fixterCNDText: fixterCNDText.value,
    reviewTitle: reviewTitle.value,
    bookingsNumberText: bookingsNumberText.value,
    yearsInBusinessText: yearsInBusinessText.value,
    showMoreGaragesToggleLabel: showMoreGaragesToggleLabel.value,
    showLessGaragesToggleLabel: showLessGaragesToggleLabel.value,
    noGaragesText: noGaragesText.value,
  };
};

export interface GarageDirectoryContentProps extends ComponentPropsWithoutRef<'div'> {
  readonly lat: AppLocation['lat'];
  readonly long: AppLocation['long'];
  readonly area: AppLocation['areaName'];
  readonly garagesList: AppGarage[];
  readonly locale: Locale;
  readonly segment: Segment;
}

const useIntersectionObserver = (className: string, onIntersect: VoidFunction, onOutOfView: VoidFunction) => {
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        onIntersect();
      } else {
        onOutOfView();
      }
    });

    const element = document.querySelector(`.${className}`);
    if (element) {
      observer.observe(element); // Observe the element with the specified class name
    }

    return () => {
      observer.disconnect(); // Cleanup observer on unmount
    };
  }, [className, onIntersect, onOutOfView]);
};

export const GarageDirectoryContent: CmsServerComponent<GarageDirectoryContentProps> =
  function GarageDirectoryContent({ lat, long, garagesList, area, data, locale, segment }): JSX.Element {
    const {
      mapPostCodeText,
      mapExpandText,
      mapHideText,
      stickyHeaderBackToMapLabel,
      stickyHeaderTitle,
      certifiedImageDesktop,
      certifiedImageMobile,
      reviewsNumberText,
      moreInfoCtaLabel,
      fixterCNDText,
      reviewTitle,
      bookingsNumberText,
      yearsInBusinessText,
      showMoreGaragesToggleLabel,
      showLessGaragesToggleLabel,
      noGaragesText,
    } = getComponentTranslations(data, area);

    const [selectedGarageId, setSelectedGarageId] = useState('');

    const [showStickyHeader, setShowStickHeader] = useState(false);

    const [bottomMap, setBottomMap] = useState(false);

    const [showAll, setShowAll] = useState(false);

    const toggleShowAll = () => {
      setShowAll(!showAll);
    };

    const deduplicateGarages = Object.values(
      garagesList.reduce((acc: any, i: any) => {
        acc[i.garageId] = i;
        return acc;
      }, {})
    ) as unknown as AppGarage[];

    const [garages, setGarages] = useState<AppGarage[]>(deduplicateGarages);

    const [selectedPosition, setSelectedPosition] = useState({
      lat: Number.parseFloat(lat),
      lng: Number.parseFloat(long),
    });

    const garageRefs: any = useRef({});

    const initalizeGarageMapRef = React.createRef<any>();

    const initializeGarageRefs = (newGaragesList: AppGarage[]) => {
      garageRefs.current = newGaragesList.reduce((acc: any, garage: any) => {
        acc[garage.garageId] = React.createRef();
        return acc;
      }, {});
    };

    const defaultNumberOfGaragesToShow = 6;
    const shouldDisplayShowAllToggle = garages.length > defaultNumberOfGaragesToShow;

    useEffect(() => {
      if (!shouldDisplayShowAllToggle) {
        setShowAll(true);
      }
    }, [shouldDisplayShowAllToggle]);

    // Initialize refs for all garages
    useEffect(() => {
      initializeGarageRefs(garagesList);
    }, [garagesList]);

    const setNewSelectedGarageIdAndScrollIntoView = (newSelectedGarageId: string) => {
      setShowAll(true);
      setSelectedGarageId(newSelectedGarageId);
      // Scroll the corresponding GDPGarageInfoCard into view
      const ref = garageRefs.current[newSelectedGarageId];

      if (ref && ref.current) {
        ref.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    };

    useIntersectionObserver(
      'GarageMap',
      () => {
        setShowStickHeader(false); // Update state when GarageMap is in view
      },
      () => {
        setShowStickHeader(true); // Update state when GarageMap is out of view
      }
    );

    useIntersectionObserver(
      'CertifiedPartnerSection',
      () => {
        setBottomMap(true); // Update state when GarageMap is in view
      },
      () => {
        setBottomMap(false); // Update state when GarageMap is out of view
      }
    );

    const fetchNewGarages = async (latitude: number, longitude: number) => {
      setSelectedPosition({ lat: latitude, lng: longitude });

      const newGaragesList = await fetch(`/api/getGaragesByLatLong`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          segment,
          lat: latitude,
          lng: longitude,
        }),
      });
      const newGarages = await newGaragesList.json();

      setGarages(newGarages);
      initializeGarageRefs(newGarages);
    };

    return (
      <div className={styles.component}>
        <div
          ref={initalizeGarageMapRef}
          className={classNames(
            styles.GarageMapPositionStycky,
            bottomMap && styles.NoGarageMapPositionStycky
          )}
        >
          <GarageMap
            selectedGarageId={selectedGarageId}
            setSelectedGarageId={setNewSelectedGarageIdAndScrollIntoView}
            className={classNames(
              garages.length < 2 && styles.GarageMapPositionShort,
              garages.length >= 2 && styles.GarageMapPosition,
              bottomMap && garages.length <= 2 && styles.BottomMapShort,
              bottomMap && garages.length > 2 && styles.BottomMap
            )}
            location={{
              name: 'UK',
              centerLatLng: selectedPosition,
              zoom: 11.5,
            }}
            setLatLong={async ({ lat: latitude, lng: longitude }) => {
              fetchNewGarages(latitude, longitude);
            }}
            garages={garages}
            translations={{
              postCodeText: mapPostCodeText,
              hideMapText: mapHideText,
              expandMapText: mapExpandText,
            }}
            locale={locale}
          />
        </div>
        {showStickyHeader && (
          <StickyHeaderScrollToMap
            onBackToMapClick={() => {
              initalizeGarageMapRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }}
            translations={{ title: stickyHeaderTitle, backToMapLabel: stickyHeaderBackToMapLabel }}
          />
        )}
        <Row>
          <Col extraSmall={4} medium={6}>
            {garages.length > 0 && (
              <div>
                <div className={classNames(styles.GarageListWrapper, showAll && styles.show)}>
                  {garages.map((garage) => (
                    <div ref={garageRefs.current[garage.garageId]} key={garage.garageId}>
                      <GDPGarageInfoCard
                        setSelectedGarageId={setSelectedGarageId}
                        selectedGarageId={selectedGarageId}
                        key={garage.garageId}
                        garage={garage}
                        translations={{
                          certifiedImageDesktop,
                          certifiedImageMobile,
                          reviewsNumberText,
                          moreInfoCtaLabel,
                          fixterCNDText,
                          reviewTitle,
                          yearsInBusinessText,
                          bookingsNumberText,
                        }}
                      />
                    </div>
                  ))}

                  {!showAll && <div className={classNames(styles.hideMask)}> </div>}
                </div>
                {shouldDisplayShowAllToggle && (
                  <ShowAllToggle
                    showAll={showAll}
                    toggleShowAll={toggleShowAll}
                    showMoreText={showMoreGaragesToggleLabel}
                    showLessText={showLessGaragesToggleLabel}
                  />
                )}
              </div>
            )}

            {garages.length === 0 && <div className={styles.NoGarages}>{noGaragesText}</div>}
          </Col>
        </Row>
      </div>
    );
  };

GarageDirectoryContent.displayName = 'GarageDirectoryContent';
GarageDirectoryContent.schema = GarageDirectoryContentSchema;
